import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Pencil, History, Clock, ArrowRight, User, FileText, Calendar } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";

export function PersonalTaskEditDialog({ task, open, onOpenChange, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (task && open) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setShowHistory(false);
      setHistory([]);
    }
  }, [task, open]);

  const fetchHistory = async () => {
    if (!task) return;
    setLoadingHistory(true);
    try {
      const res = await apiFetch(`/personal-tasks/${task.task_id}/history`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.modifications || []);
      }
    } catch {
      toast.error("Errore nel caricamento dello storico");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleToggleHistory = () => {
    if (!showHistory && history.length === 0) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Il titolo non può essere vuoto");
      return;
    }

    const changes = {};
    if (title !== task.title) changes.title = title;
    if (description !== (task.description || "")) changes.description = description;

    if (Object.keys(changes).length === 0) {
      toast.info("Nessuna modifica rilevata");
      onOpenChange(false);
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch(`/personal-tasks/${task.task_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changes),
      });
      if (res.ok) {
        toast.success("Task aggiornato!");
        onOpenChange(false);
        onSaved?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'aggiornamento");
      }
    } catch {
      toast.error("Errore nell'aggiornamento del task");
    } finally {
      setSaving(false);
    }
  };

  const getFieldLabel = (field) => {
    const labels = { title: "Titolo", description: "Descrizione", due_date: "Scadenza", status: "Stato" };
    return labels[field] || field;
  };

  const getFieldIcon = (field) => {
    if (field === "title") return <FileText className="w-3 h-3" />;
    if (field === "description") return <FileText className="w-3 h-3" />;
    if (field === "due_date") return <Calendar className="w-3 h-3" />;
    return <Pencil className="w-3 h-3" />;
  };

  const formatValue = (field, value) => {
    if (!value) return "(vuoto)";
    if (field === "due_date") {
      try {
        return new Date(value).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
      } catch {
        return value;
      }
    }
    if (field === "status") {
      const statusLabels = { da_fare: "Da fare", in_corso: "In corso", completato: "Completato" };
      return statusLabels[value] || value;
    }
    return value.length > 60 ? value.substring(0, 60) + "..." : value;
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil className="w-5 h-5 text-indigo-600" />
            Modifica Task Personale
          </DialogTitle>
        </DialogHeader>

        {/* Client badge */}
        <div className="mb-2">
          <Badge className="bg-indigo-100 text-indigo-800 font-bold text-xs" data-testid="edit-task-client-badge">
            {task.client_name || "Cliente non assegnato"}
          </Badge>
        </div>

        {/* Edit form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-task-title">Titolo *</Label>
            <Input
              id="edit-task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titolo del task"
              data-testid="edit-task-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-task-description">Descrizione</Label>
            <Textarea
              id="edit-task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrizione (opzionale)"
              rows={3}
              data-testid="edit-task-description-input"
            />
          </div>
        </div>

        {/* History toggle */}
        <div className="mt-4 border-t pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleHistory}
            className="text-slate-600 hover:text-indigo-600 w-full justify-start"
            data-testid="toggle-history-btn"
          >
            <History className="w-4 h-4 mr-2" />
            {showHistory ? "Nascondi storico modifiche" : "Mostra storico modifiche"}
          </Button>

          {showHistory && (
            <div className="mt-3 space-y-2 max-h-[250px] overflow-y-auto" data-testid="task-history-list">
              {loadingHistory ? (
                <p className="text-sm text-muted-foreground text-center py-3">Caricamento...</p>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3">
                  Nessuna modifica registrata
                </p>
              ) : (
                history.slice().reverse().map((mod, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-sm"
                    data-testid={`history-entry-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-3 h-3 text-indigo-500" />
                      <span className="font-medium text-indigo-700">{mod.modified_by_name || "Sconosciuto"}</span>
                      <span className="text-muted-foreground">-</span>
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-muted-foreground">
                        {new Date(mod.modified_at).toLocaleString("it-IT", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit"
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {getFieldIcon(mod.field_changed)}
                      <Badge variant="outline" className="text-xs">{getFieldLabel(mod.field_changed)}</Badge>
                      <span className="text-xs text-red-600 line-through">
                        {formatValue(mod.field_changed, mod.old_value)}
                      </span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-emerald-700 font-medium">
                        {formatValue(mod.field_changed, mod.new_value)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-task-cancel-btn">
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="edit-task-save-btn"
          >
            {saving ? "Salvataggio..." : "Salva Modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
